/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

import React, { useState } from 'react';

const C = {
  navy:        '#0f1f3d',
  navyMid:     '#162d52',
  navyLight:   '#1e3a6e',
  navyBorder:  '#253d6e',
  orange:      '#F97316',
  white:       '#ffffff',
  slate:       '#94a3b8',
  slateLight:  '#cbd5e1',
  slateLighter:'#e2e8f0',
  green:       '#22c55e',
  amber:       '#fbbf24',
};

const MONO = "'Consolas', 'JetBrains Mono', 'Fira Code', monospace";

// ── Data ───────────────────────────────────────────────────────────────────

const SHORTCUTS = [
  { keys: ['Ctrl', 'S'],    action: 'Save project' },
  { keys: ['Ctrl', 'Z'],    action: 'Undo last action' },
  { keys: ['Ctrl', 'Y'],    action: 'Redo' },
  { keys: ['Ctrl', 'F'],    action: 'Find in current view' },
  { keys: ['Ctrl', 'A'],    action: 'Select all' },
  { keys: ['Ctrl', 'C'],    action: 'Copy selected' },
  { keys: ['Ctrl', 'V'],    action: 'Paste' },
  { keys: ['F2'],           action: 'Rename selected item' },
  { keys: ['Del'],          action: 'Delete selected' },
  { keys: ['F5'],           action: 'Download All to device' },
  { keys: ['F6'],           action: 'Download Individual Address' },
  { keys: ['F7'],           action: 'Download Application only' },
  { keys: ['F8'],           action: 'Download Parameters only' },
  { keys: ['Alt', 'Enter'], action: 'Open item Properties' },
  { keys: ['Esc'],          action: 'Cancel / close dialog' },
  { path: 'Commissioning → Bus Monitor',   action: 'Open Bus Monitor' },
  { path: 'Commissioning → Group Monitor', action: 'Open Group Monitor' },
  { path: 'Commissioning → Diagnostics',   action: 'Device info & diagnostics' },
];

const DPT_TYPES = [
  { dpt: '1.001',   type: '1-bit',    range: '0 = Off  ·  1 = On',          use: 'Switching, relay control' },
  { dpt: '1.008',   type: '1-bit',    range: '0 = Up  ·  1 = Down',         use: 'Blind / shutter direction' },
  { dpt: '1.009',   type: '1-bit',    range: '0 = Open  ·  1 = Close',      use: 'Valve open / close' },
  { dpt: '3.007',   type: '4-bit',    range: 'Step + direction',             use: 'Relative dimming (hold)' },
  { dpt: '5.001',   type: '1-byte %', range: '0 – 100 %',                   use: 'Dim level · valve position' },
  { dpt: '5.010',   type: '1-byte',   range: '0 – 255',                     use: 'Scene number recall' },
  { dpt: '7.001',   type: '2-byte',   range: '0 – 65,535',                  use: 'Pulse counter · meter' },
  { dpt: '9.001',   type: '2-byte F', range: '−273 … +670 °C',              use: 'Temperature sensor / SP' },
  { dpt: '9.004',   type: '2-byte F', range: '0 … 670,760 lx',             use: 'Brightness / daylight' },
  { dpt: '9.007',   type: '2-byte F', range: '0 … 670 %RH',                use: 'Relative humidity' },
  { dpt: '18.001',  type: 'Scene',    range: '0–63 activate  ·  +128 store', use: 'Scene recall & store' },
  { dpt: '20.102',  type: 'HVAC',     range: '0=Auto 1=Comf 2=Stby 3=Eco',  use: 'Heating / cooling mode' },
  { dpt: '232.600', type: '3-byte',   range: 'R 0-255 · G 0-255 · B 0-255', use: 'LED RGB colour' },
];

const BUS_CHECKS = [
  { id: 'b0',  label: 'Bus voltage at PSU output: 29 V DC ±1 V',                      note: 'Measure KNX+ to KNX− with multimeter' },
  { id: 'b1',  label: 'Bus voltage under load at furthest device: > 27 V DC',          note: null },
  { id: 'b2',  label: 'PSU current: Σ device bus currents × 1.2 ≤ PSU rating',         note: 'Avg device draw: 5–20 mA; add 20% headroom' },
  { id: 'b3',  label: 'Max 64 devices per segment (without additional PSU)',            note: null },
  { id: 'b4',  label: 'Segment cable length ≤ 1,000 m total',                          note: null },
  { id: 'b5',  label: 'Device-to-device distance ≤ 700 m',                             note: null },
  { id: 'b6',  label: 'Cable type: YCYM 2×2×0.8 mm or approved equivalent',            note: null },
  { id: 'b7',  label: 'Separation from mains: ≥ 4 mm from 230 V cables',              note: 'In shared conduit or trunking' },
  { id: 'b8',  label: 'Line choke fitted between PSU output and bus cable',             note: null },
  { id: 'b9',  label: 'ETS Bus Diagnostics bus load < 25% (< ~1,000 telegrams/min)',   note: null },
  { id: 'b10', label: 'ETS Bus Monitor: no ACK timeout or bus collision errors',        note: null },
  { id: 'b11', label: 'ETS Topology: no duplicate physical addresses on any line',     note: null },
];

const COMMISSION_STEPS = [
  { id: 'c0',  label: 'Create ETS6 project, assign to KNX Partner account No. 20248',  note: null },
  { id: 'c1',  label: 'Define building structure: Building → Floor → Room',             note: null },
  { id: 'c2',  label: 'Import device .knxprod databases from manufacturer catalogues',  note: 'Catalogue tab in ETS6' },
  { id: 'c3',  label: 'Add devices to topology and prepare PA map before site visit',   note: null },
  { id: 'c4',  label: 'On site: connect USB or IP interface, verify 29 V DC bus',       note: null },
  { id: 'c5',  label: 'Individual-address each device via programming button (F6)',     note: 'One device at a time' },
  { id: 'c6',  label: 'Download full application to all devices (F5 → Download All)',  note: 'PA + App + Parameters' },
  { id: 'c7',  label: 'Define GA structure (2-level or 3-level) and naming convention', note: null },
  { id: 'c8',  label: 'Assign communication objects to group addresses in GA workspace', note: 'Verify DPT match on all COs' },
  { id: 'c9',  label: 'Download updated GA configuration to all affected devices',      note: null },
  { id: 'c10', label: 'Programme line coupler filter tables and download to couplers',  note: null },
  { id: 'c11', label: 'Configure logic controller: scenes, timers, astro clock',        note: null },
  { id: 'c12', label: 'Run ETS Bus Diagnostics — bus load < 25%, zero errors',         note: null },
  { id: 'c13', label: 'Room-by-room function test against client sign-off sheet',       note: null },
  { id: 'c14', label: 'Verify failsafe behaviour (e.g. blinds lower on bus loss)',      note: null },
  { id: 'c15', label: 'Export .knxproj and archive — minimum 2 backup copies',         note: 'Client USB + company server' },
];

// ── Helper components ──────────────────────────────────────────────────────

function SectionCard({ num, title, children }) {
  return (
    <div style={{
      background: C.navyMid,
      border: `1.5px solid ${C.navyBorder}`,
      borderRadius: 10,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        background: `linear-gradient(90deg, ${C.navyLight} 0%, ${C.navyMid} 100%)`,
        borderBottom: `2px solid ${C.orange}`,
        padding: '8px 14px',
        display: 'flex', alignItems: 'center', gap: 9,
        flexShrink: 0,
      }}>
        <div style={{
          background: C.orange, color: C.white,
          width: 22, height: 22, borderRadius: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800, flexShrink: 0,
        }}>
          {num}
        </div>
        <span style={{ fontWeight: 700, fontSize: 13, color: C.white, letterSpacing: '-0.2px' }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '12px 14px', flex: 1 }}>
        {children}
      </div>
    </div>
  );
}

function KeyCap({ children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: C.navyLight,
      border: `1px solid ${C.navyBorder}`,
      borderBottom: `2px solid ${C.navyBorder}`,
      borderRadius: 4,
      padding: '0 6px',
      minWidth: 22, height: 19,
      fontSize: 10, fontFamily: MONO, fontWeight: 700,
      color: C.orange, letterSpacing: 0, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

function MenuPath({ path }) {
  const parts = path.split('→').map(s => s.trim());
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color: C.slate, fontSize: 10 }}>›</span>}
          <span style={{
            fontSize: 10, fontFamily: MONO, color: C.amber,
            background: `${C.amber}18`, border: `1px solid ${C.amber}33`,
            borderRadius: 3, padding: '0 5px',
          }}>
            {part}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

function CheckRow({ item, isChecked, onToggle }) {
  return (
    <div
      onClick={() => onToggle(item.id)}
      style={{
        display: 'flex', gap: 9, alignItems: 'flex-start',
        padding: '5px 0',
        borderBottom: `1px solid ${C.navyBorder}`,
        cursor: 'pointer',
        opacity: isChecked ? 0.55 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      <div style={{
        width: 15, height: 15, borderRadius: 3, flexShrink: 0, marginTop: 1,
        background: isChecked ? C.green : 'transparent',
        border: `1.5px solid ${isChecked ? C.green : C.navyBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, color: C.white, fontWeight: 700,
        transition: 'all 0.15s',
      }}>
        {isChecked ? '✓' : ''}
      </div>
      <div style={{ flex: 1 }}>
        <span style={{
          fontSize: 12.5, color: isChecked ? C.slate : C.slateLighter,
          textDecoration: isChecked ? 'line-through' : 'none',
          lineHeight: 1.4, transition: 'color 0.15s',
        }}>
          {item.label}
        </span>
        {item.note && (
          <div style={{ fontSize: 10.5, color: C.slate, marginTop: 2 }}>{item.note}</div>
        )}
      </div>
    </div>
  );
}

function ChecklistProgress({ items, checked, prefix, onClear }) {
  const done  = items.filter(i => checked[i.id]).length;
  const total = items.length;
  const pct   = Math.round((done / total) * 100);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{ flex: 1, height: 4, background: C.navyBorder, borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: done === total && total > 0 ? C.green : C.orange,
          borderRadius: 2, transition: 'width 0.2s',
        }} />
      </div>
      <span style={{
        fontSize: 11, fontWeight: 600, flexShrink: 0,
        color: done === total && total > 0 ? C.green : C.slate,
      }}>
        {done === total && total > 0 ? 'Complete ✓' : `${done} / ${total}`}
      </span>
      {done > 0 && (
        <button onClick={onClear} style={{
          background: 'none', border: `1px solid ${C.navyBorder}`,
          color: C.slate, fontSize: 10, cursor: 'pointer',
          borderRadius: 4, padding: '2px 8px', flexShrink: 0,
        }}>
          Reset
        </button>
      )}
    </div>
  );
}

// ── Address diagram ────────────────────────────────────────────────────────

function AddressDiagram({ parts, sep }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, marginBottom: 12 }}>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <div style={{
              fontSize: 18, color: C.orange, fontFamily: MONO,
              padding: '8px 3px 0', fontWeight: 700, lineHeight: 1, flexShrink: 0,
            }}>
              {sep}
            </div>
          )}
          <div style={{
            flex: 1, textAlign: 'center',
            background: `${C.navyLight}88`,
            border: `1.5px solid ${C.navyBorder}`,
            borderRadius: 7, padding: '7px 8px',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.orange, fontFamily: MONO, lineHeight: 1 }}>
              {p.example}
            </div>
            <div style={{ fontSize: 10, color: C.white, fontWeight: 700, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {p.label}
            </div>
            <div style={{ fontSize: 10, color: C.slate, marginTop: 2, fontFamily: MONO }}>
              {p.range}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function ETS6QuickReference() {
  const [checked, setChecked] = useState({});

  function toggle(id) {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function clearSection(ids) {
    setChecked(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  }

  return (
    <div style={{
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      background: C.navy,
      minHeight: '100vh',
      color: C.white,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* ── Header ── */}
      <div style={{
        background: C.navyMid,
        borderBottom: `3px solid ${C.orange}`,
        padding: '0.875rem 1.75rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: C.orange, color: C.white, fontWeight: 800,
            fontSize: 17, padding: '4px 10px', borderRadius: 6, letterSpacing: '-0.5px',
          }}>
            BB
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              Briq-n-Braq
            </div>
            <div style={{ color: C.slate, fontSize: 11, marginTop: 1 }}>
              ETS6 Quick Reference
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: C.orange, fontWeight: 600, fontSize: 12.5 }}>
            KNX Partner No. 20248
          </div>
          <div style={{ color: C.slate, fontSize: 11, marginTop: 2 }}>
            ETS6 · KNX TP · KNXnet/IP
          </div>
        </div>
      </div>

      {/* ── Intro ── */}
      <div style={{ padding: '1rem 1.75rem 0.5rem', flexShrink: 0 }}>
        <h1 style={{ margin: '0 0 0.25rem', fontSize: 20, fontWeight: 700, letterSpacing: '-0.4px' }}>
          ETS6 Quick Reference Card
        </h1>
        <p style={{ margin: 0, color: C.slate, fontSize: 12.5 }}>
          Keyboard shortcuts, address formats, DPTs, and commissioning checklists for KNX professionals.
        </p>
      </div>

      {/* ── Reference grid ── */}
      <div style={{
        padding: '1rem 1.75rem 1.5rem',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
        alignItems: 'start',
        flex: 1,
      }}>

        {/* ── 1. Keyboard Shortcuts ── */}
        <SectionCard num="1" title="Keyboard Shortcuts">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {SHORTCUTS.map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 8, padding: '4px 0',
                borderBottom: `1px solid ${C.navyBorder}`,
              }}>
                <span style={{ fontSize: 12.5, color: C.slateLighter, flex: 1 }}>{s.action}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                  {s.keys
                    ? s.keys.map((k, j) => (
                        <React.Fragment key={j}>
                          {j > 0 && <span style={{ fontSize: 9, color: C.slate }}>+</span>}
                          <KeyCap>{k}</KeyCap>
                        </React.Fragment>
                      ))
                    : <MenuPath path={s.path} />
                  }
                </div>
              </div>
            ))}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 10.5, color: C.slate, lineHeight: 1.5 }}>
            Shortcuts shown for ETS6 on Windows. Menu-path items have no dedicated shortcut; navigate via the ribbon.
          </p>
        </SectionCard>

        {/* ── 2. Physical Address Structure ── */}
        <SectionCard num="2" title="Physical Address Structure">
          <AddressDiagram
            sep="."
            parts={[
              { label: 'Area',   range: '1 – 15',  example: '1' },
              { label: 'Line',   range: '1 – 15',  example: '2' },
              { label: 'Device', range: '1 – 255', example: '15' },
            ]}
          />

          {/* Limits table */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Topology Limits
            </div>
            {[
              ['Max areas',                        '15'],
              ['Max lines per area',               '15'],
              ['Max devices per line',             '255 (64 without extra PSU)'],
              ['Max total devices',                '15 × 15 × 255 = 57,375'],
              ['Max segment cable length',         '1,000 m'],
              ['Max device-to-device distance',    '700 m'],
            ].map(([label, val], i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', gap: 8,
                padding: '3.5px 0', borderBottom: `1px solid ${C.navyBorder}`,
                fontSize: 12.5,
              }}>
                <span style={{ color: C.slateLight }}>{label}</span>
                <span style={{ color: C.white, fontFamily: MONO, fontSize: 11.5, fontWeight: 600 }}>{val}</span>
              </div>
            ))}
          </div>

          {/* Special addresses */}
          <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
            Special Addresses
          </div>
          {[
            ['A.0.0', 'Area coupler (on backbone line)'],
            ['A.L.0', 'Line coupler (top of sub-line L)'],
            ['0.0.0', 'Unassigned / factory default'],
          ].map(([addr, desc], i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '3.5px 0', borderBottom: `1px solid ${C.navyBorder}`,
            }}>
              <span style={{ fontFamily: MONO, fontSize: 12, color: C.orange, fontWeight: 700, flexShrink: 0, minWidth: 44 }}>
                {addr}
              </span>
              <span style={{ fontSize: 12.5, color: C.slateLight }}>{desc}</span>
            </div>
          ))}

          {/* Examples */}
          <div style={{ marginTop: 10, padding: '8px 10px', background: `${C.navy}88`, borderRadius: 7, border: `1px solid ${C.navyBorder}` }}>
            <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Examples
            </div>
            {[
              ['1.1.1',  'First device — Area 1, Line 1'],
              ['1.1.5',  'Push button on main line, device 5'],
              ['2.3.12', 'Actuator — Area 2, Line 3, slot 12'],
              ['1.2.0',  'Line coupler for sub-line 2 in area 1'],
            ].map(([addr, desc], i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: i < 3 ? 3 : 0 }}>
                <span style={{ fontFamily: MONO, fontSize: 12, color: C.amber, fontWeight: 700, minWidth: 50, flexShrink: 0 }}>{addr}</span>
                <span style={{ fontSize: 12, color: C.slateLight }}>{desc}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── 3. Group Address Structure ── */}
        <SectionCard num="3" title="Group Address Structure">
          {/* 3-level */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7 }}>
              3-Level Format (recommended)
            </div>
            <AddressDiagram
              sep="/"
              parts={[
                { label: 'Main',   range: '0 – 31',  example: '1' },
                { label: 'Middle', range: '0 – 7',   example: '0' },
                { label: 'Sub',    range: '0 – 255', example: '5' },
              ]}
            />
            <div style={{ fontSize: 11.5, color: C.slate, marginBottom: 8 }}>
              Max 31 × 8 × 256 = <span style={{ color: C.white, fontWeight: 600 }}>63,488 group addresses</span>
            </div>
            {[
              { ga: '1/0/1',  desc: 'GF Lighting / Living Room / Switch' },
              { ga: '1/0/2',  desc: 'GF Lighting / Living Room / Dim level' },
              { ga: '2/0/1',  desc: 'HVAC / Zone 1 / Temperature setpoint' },
              { ga: '2/0/2',  desc: 'HVAC / Zone 1 / Actual temperature (status)' },
              { ga: '3/0/1',  desc: 'Blinds / LR Window / Move up-down' },
              { ga: '3/0/2',  desc: 'Blinds / LR Window / Stop / position' },
            ].map(({ ga, desc }, i) => (
              <div key={i} style={{
                display: 'flex', gap: 10, padding: '3px 0',
                borderBottom: `1px solid ${C.navyBorder}`,
              }}>
                <span style={{ fontFamily: MONO, fontSize: 12, color: C.amber, fontWeight: 700, minWidth: 44, flexShrink: 0 }}>{ga}</span>
                <span style={{ fontSize: 12, color: C.slateLight }}>{desc}</span>
              </div>
            ))}
          </div>

          {/* 2-level */}
          <div>
            <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7 }}>
              2-Level Format
            </div>
            <AddressDiagram
              sep="/"
              parts={[
                { label: 'Main', range: '0 – 31',   example: '1' },
                { label: 'Sub',  range: '0 – 2047', example: '5' },
              ]}
            />
            <div style={{ fontSize: 11.5, color: C.slate, marginBottom: 8 }}>
              Max 31 × 2,048 = <span style={{ color: C.white, fontWeight: 600 }}>63,488 group addresses</span>
            </div>
            {[
              { ga: '1/1',  desc: 'Lighting / Circuit 1' },
              { ga: '1/2',  desc: 'Lighting / Circuit 2 dim level' },
              { ga: '2/1',  desc: 'HVAC / Setpoint zone 1' },
            ].map(({ ga, desc }, i) => (
              <div key={i} style={{
                display: 'flex', gap: 10, padding: '3px 0',
                borderBottom: `1px solid ${C.navyBorder}`,
              }}>
                <span style={{ fontFamily: MONO, fontSize: 12, color: C.amber, fontWeight: 700, minWidth: 32, flexShrink: 0 }}>{ga}</span>
                <span style={{ fontSize: 12, color: C.slateLight }}>{desc}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10, padding: '8px 10px', background: `${C.navy}88`, borderRadius: 7, border: `1px solid ${C.navyBorder}` }}>
            <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>
              Best Practice
            </div>
            {[
              'Separate sending GAs from status/feedback GAs',
              'Agree naming convention before assigning any GAs',
              'Reserve a GA block (e.g. 31/7/x) for diagnostics',
              'Document every GA in a spreadsheet before download',
            ].map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: 7, fontSize: 12, color: C.slateLight, marginBottom: i < 3 ? 4 : 0 }}>
                <span style={{ color: C.orange, flexShrink: 0 }}>▪</span>{tip}
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── 4. Common Data Point Types ── */}
        <SectionCard num="4" title="Common Data Point Types (DPTs)">
          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '52px 60px 1fr 1fr',
            gap: '0 8px',
            padding: '0 0 5px',
            borderBottom: `1px solid ${C.orange}44`,
            marginBottom: 2,
          }}>
            {['DPT', 'Format', 'Range / Values', 'Typical Use'].map((h, i) => (
              <div key={i} style={{ fontSize: 9.5, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {h}
              </div>
            ))}
          </div>

          {DPT_TYPES.map((d, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '52px 60px 1fr 1fr',
              gap: '0 8px',
              padding: '4px 0',
              borderBottom: `1px solid ${C.navyBorder}`,
              alignItems: 'start',
            }}>
              <span style={{ fontFamily: MONO, fontSize: 11.5, color: C.amber, fontWeight: 700 }}>{d.dpt}</span>
              <span style={{ fontSize: 11, color: C.slate }}>{d.type}</span>
              <span style={{ fontSize: 11, color: C.slateLight, lineHeight: 1.4 }}>{d.range}</span>
              <span style={{ fontSize: 11, color: C.slateLighter, lineHeight: 1.4 }}>{d.use}</span>
            </div>
          ))}

          <p style={{ margin: '8px 0 0', fontSize: 10.5, color: C.slate, lineHeight: 1.5 }}>
            Sender and receiver COs must share the same DPT. Mismatches cause telegrams to be silently ignored or misinterpreted.
          </p>
        </SectionCard>

        {/* ── 5. Bus Diagnostics Checklist ── */}
        <SectionCard num="5" title="Bus Diagnostics Checklist">
          <ChecklistProgress
            items={BUS_CHECKS}
            checked={checked}
            onClear={() => clearSection(BUS_CHECKS.map(i => i.id))}
          />

          {BUS_CHECKS.map(item => (
            <CheckRow
              key={item.id}
              item={item}
              isChecked={!!checked[item.id]}
              onToggle={toggle}
            />
          ))}

          {/* PSU sizing formula */}
          <div style={{ marginTop: 10, padding: '8px 10px', background: `${C.navy}88`, borderRadius: 7, border: `1px solid ${C.navyBorder}` }}>
            <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>
              PSU Sizing Formula
            </div>
            <div style={{ fontFamily: MONO, fontSize: 12, color: C.amber, marginBottom: 5 }}>
              Σ (device mA) × 1.2 ≤ PSU rating
            </div>
            <div style={{ fontSize: 12, color: C.slateLight, lineHeight: 1.5 }}>
              Example: 40 devices × 10 mA avg = 400 mA × 1.2 = 480 mA → select <span style={{ color: C.white, fontWeight: 600 }}>640 mA PSU</span>
            </div>
          </div>
        </SectionCard>

        {/* ── 6. ETS6 Commissioning Checklist ── */}
        <SectionCard num="6" title="ETS6 Commissioning Checklist">
          <ChecklistProgress
            items={COMMISSION_STEPS}
            checked={checked}
            onClear={() => clearSection(COMMISSION_STEPS.map(i => i.id))}
          />

          {COMMISSION_STEPS.map((item, i) => (
            <div key={item.id} style={{
              display: 'flex', gap: 9, alignItems: 'flex-start',
              padding: '5px 0',
              borderBottom: `1px solid ${C.navyBorder}`,
              cursor: 'pointer',
              opacity: checked[item.id] ? 0.55 : 1,
              transition: 'opacity 0.15s',
            }} onClick={() => toggle(item.id)}>
              <div style={{
                width: 15, height: 15, borderRadius: 3, flexShrink: 0, marginTop: 1,
                background: checked[item.id] ? C.green : 'transparent',
                border: `1.5px solid ${checked[item.id] ? C.green : C.navyBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: C.white, fontWeight: 700,
                transition: 'all 0.15s',
              }}>
                {checked[item.id] ? '✓' : ''}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                  <span style={{
                    flexShrink: 0, width: 16, fontSize: 10.5, fontWeight: 700,
                    color: checked[item.id] ? C.slate : C.orange, fontFamily: MONO,
                    marginTop: 1,
                  }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span style={{
                    fontSize: 12.5, color: checked[item.id] ? C.slate : C.slateLighter,
                    textDecoration: checked[item.id] ? 'line-through' : 'none',
                    lineHeight: 1.4, transition: 'color 0.15s',
                  }}>
                    {item.label}
                  </span>
                </div>
                {item.note && (
                  <div style={{ fontSize: 10.5, color: C.slate, marginTop: 2, paddingLeft: 23 }}>
                    {item.note}
                  </div>
                )}
              </div>
            </div>
          ))}
        </SectionCard>

      </div>

      {/* ── Footer ── */}
      <div style={{
        padding: '0.75rem 1.75rem',
        background: C.navyMid,
        borderTop: `2px solid ${C.navyBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11.5, color: C.slate, flexShrink: 0, flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <span style={{ color: C.white, fontWeight: 600 }}>Briq-n-Braq</span>
          {' · '}KNX Certified Partner No.{' '}
          <span style={{ color: C.orange, fontWeight: 600 }}>20248</span>
        </div>
        <div>ETS6 · KNX TP · KNXnet/IP · IEC 14543-3</div>
      </div>
    </div>
  );
}
